import {observable, action, computed, ObservableMap, makeObservable} from 'mobx';
import {itemTypeEnum} from "../enums";
import {ProjectRequester} from "./RequestApi/ProjectRequester";
import {ProcedureRequester} from "./RequestApi/ProcedureRequester";
import {generalStore} from "../index";
import {DATA_INTERVAL} from "./DataStore";


export class DragStore {

    constructor(dataStore) {
        makeObservable(this, {
            dragItemId: observable,
            dropItemId: observable,
            isBlocked: observable,
            controlledDelta: observable,
            selectedProceduresIds: observable,
            selectedFilesIds: observable,
            selectedFoldersIds: observable,
            recentlyUploadedFiles: observable,
            isDraggingFiles: observable,
            multipleSelected: computed,
            resetDragMedia: action,
            drop: action
        });

        this.dataStore = dataStore;
    }

    dragItemId = false;
    dropItemId = null;
    isBlocked = false;
    controlledDelta = {x: 0, y: 0};
    selectedProceduresIds = new Set();
    selectedFilesIds = new Set();
    selectedFoldersIds = new Set();
    recentlyUploadedFiles = new Set();
    isDraggingFiles = false;

    get multipleSelected() {
        return this.selectedFoldersIds.size + this.selectedProceduresIds.size > 1;
    }

    resetDragMedia = () => {
        this.selectedProceduresIds = new Set();
        this.selectedFilesIds = new Set();
        this.selectedFoldersIds = new Set();
        this.recentlyUploadedFiles = new Set();
    };

    drop = async (draggedItemId, draggedItemType, projectId, procedureId) => {
        if (!this.dropItemId) {
            return;
        }
        let success;
        const filesIds = [], foldersIds = [], proceduresIds = [];

        switch (draggedItemType) {
            case itemTypeEnum.FOLDER:
                foldersIds.push(draggedItemId);
                success = await this.moveItems(proceduresIds, foldersIds, projectId);
                break;

            case itemTypeEnum.PROCEDURE:
                proceduresIds.push(draggedItemId);
                success = await this.moveItems(proceduresIds, foldersIds, projectId);
                break;

            case itemTypeEnum.FILE:
                filesIds.push(draggedItemId);
                success = await this.moveMedia(filesIds, foldersIds, projectId);
                break;

            case itemTypeEnum.MEDIA_FOLDER:
                foldersIds.push(draggedItemId);
                success = await this.moveMedia(filesIds, foldersIds, projectId);
                break;

            case itemTypeEnum.INTERACTION_FOLDER:
                this.moveInteractionFolder(this.dragItemId, this.dropItemId, projectId, procedureId);
                success = true;
                break;

            case itemTypeEnum.INTERACTION:
                this.moveInteraction(this.dragItemId, this.dropItemId, projectId, procedureId);
                success = true;
                break;

            default:
                success = false;
        }

        return success;
    };

    moveMedia = async (filesIds, foldersIds, projectId) => {
        this.selectedFilesIds.forEach(slaveFileId => {
            if (!filesIds.includes(slaveFileId)) {
                filesIds.push(slaveFileId);
            }
        })
        this.selectedFoldersIds.forEach(slaveFolderId => {
            if (!foldersIds.includes(slaveFolderId)) {
                foldersIds.push(slaveFolderId);
            }
        });

        const filesMovingObj = {
            parentFolderId: this.dropItemId,
            filesIds
        }

        const foldersMovingObj = {
            parentFolderId: this.dropItemId,
            foldersIds,
            projectId
        }

        const promises = [];
        if (!!filesMovingObj.filesIds.length) {
            promises.push(ProjectRequester.moveFiles(filesMovingObj, generalStore.workspace));
        }
        if (!!foldersMovingObj.foldersIds.length) {
            promises.push(ProjectRequester.moveMediaFolders(foldersMovingObj, generalStore.workspace));
        }

        try {
            await Promise.all(promises);
            return true;
        } catch (e) {
            console.error(e);
            return false;
        }
    }

    moveItems = async (proceduresIds, foldersIds, projectId) => {
        const projectFoldersMap = this.dataStore.projectsFoldersMap.get(projectId) || new ObservableMap();
        const projectProceduresMap = this.dataStore.projectsProceduresMap.get(projectId) || new ObservableMap();

        this.selectedProceduresIds.forEach(slaveProcedureId => {
            if (!proceduresIds.includes(slaveProcedureId)) {
                proceduresIds.push(slaveProcedureId);
            }
        })
        this.selectedFoldersIds.forEach(slaveFolderId => {
            if (!foldersIds.includes(slaveFolderId)) {
                foldersIds.push(slaveFolderId);
            }
        });

        const proceduresMovingObj = {
            parentFolderId: this.dropItemId,
            proceduresIds,
            projectId
        }

        const foldersMovingObj = {
            parentFolderId: this.dropItemId,
            foldersIds,
            projectId
        }
        try {
            const [savedProcedures, savedFolders] = await Promise.all([
                ProjectRequester.moveProcedures(proceduresMovingObj, generalStore.workspace),
                ProjectRequester.moveFolders(foldersMovingObj, generalStore.workspace)
            ]);

            savedProcedures.forEach(savedProcedure => projectProceduresMap.set(savedProcedure.ProcedureIdentifier, savedProcedure));
            savedFolders.forEach(savedFolder => projectFoldersMap.set(savedFolder.FolderIdentifier, savedFolder));

            this.dataStore.set('projectsProceduresMap', projectId, projectProceduresMap);
            this.dataStore.set('projectsProceduresExpirationMap', projectId, Date.now() + DATA_INTERVAL);
            this.dataStore.set('projectsFoldersMap', projectId, projectFoldersMap);
            this.dataStore.set('projectsFoldersExpirationsMap', projectId, Date.now() + DATA_INTERVAL);

            return true;
        } catch (e) {
            console.error(e);
            return false;
        }
    }

    moveInteraction = (interactionId, folderPath, projectId, procedureId) => {
        const interactionJson = {
            InteractiveStepID: interactionId,
            InteractiveIOPath: folderPath.split('/').slice(2).join('/'),
            ProcedureRef: procedureId,
            ProjectRef: projectId
        }
        ProcedureRequester.upsertInteraction(generalStore.workspace, interactionJson).catch(console.error);
    }

    moveInteractionFolder = (currentFolderPath, destinationFolderPath, projectId, procedureId) => {
        const currentPathWithoutRoot = currentFolderPath.split('/').slice(2).join('/');
        let destinationPathWithoutRoot = destinationFolderPath.split('/').slice(2).join('/');
        if (!!currentPathWithoutRoot.length) {
            const currentName = currentPathWithoutRoot.split('/').pop();
            destinationPathWithoutRoot += (!!destinationPathWithoutRoot.length ? '/' : '') + currentName;
        }
        ProcedureRequester.updateFolderPaths(generalStore.workspace, currentPathWithoutRoot, destinationPathWithoutRoot, projectId,generalStore.isOpenFromEditor ? generalStore.selectedAnimationModelId : procedureId).catch(console.error);
    }
}
